/**
 * Typed fetch client.
 *
 * Every response is validated against a Zod schema (V2-405). Error
 * responses use the standardized `Error` envelope from data-api.
 */
import { z } from "zod";

import {
  ActionModes,
  ActivationPlan,
  ArchiveSegment,
  StorageJob,
  StorageSettings,
  StorageSettingsResponse,
  StorageStatus,
  type StorageAction,
} from "./storageSchemas";

import {
  AnalyticsWindowDetail,
  AnalyticsWindowPage,
  AnalyticsSummary,
  ApiError,
  ConfigActivation,
  ConfigPruneResult,
  ConfigVersion,
  ConfigVersionPage,
  ConfigVersionStatsResponse,
  GrafanaDashboardDto,
  GrafanaDashboardListResp,
  Health,
  LiveMarketsList,
  LiveWindow,
  BotStatus,
  Direction,
  Market,
  MarketActiveConfig,
  MarketConfigActivation,
  MarketList,
  OkResp,
  ManualCommandResult,
  ModalReadiness,
  SimulationGateTimings,
  ModalResumeRequest,
  OrderDetail,
  OrderPage,
  OutcomeProposal,
  RunningStats,
  SyncRunResp,
  SyncStatus,
  SimulationDeleteResp,
  SimulationGateAblation,
  type SimulationGateAblationMode,
  SimulationOutcomeTrace,
  SimulationRun,
  SimulationRunRequest,
  TicksResp,
  ChecksResp,
  VisibilityResp,
  WindowVisibilityResp,
  WindowDetail,
  WindowPage,
  type BetStatus,
} from "./schemas";
import { KronosJob, KronosSignaturesResponse, KronosStatusResponse } from "./kronosSchemas";

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, unknown>;
  readonly requestId?: string;
  constructor(
    status: number, code: string, message: string,
    details?: Record<string, unknown>, requestId?: string,
  ) {
    super(message);
    this.status = status; this.code = code;
    this.details = details; this.requestId = requestId;
  }
}

export interface ApiClientOpts {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
}

const DEFAULT_BASE = ""; // same-origin in dev (proxied) and prod build

// ─── V3.1 fleet: global agent filter ─────────────────────────────────────────
//
// The AppShell agent selector (hooks/useAgentSelector.tsx) pushes the
// selected agents + their resolved per-market config versions here. GET
// requests to the relevant data-api endpoints are transparently scoped:
//   - /v2/orders*, /v2/windows*, /v2/analytics*,
//     /v2/stats/analytics                → agent_id=<id1>,<id2>   (per-Pi view)
// Scoping is by the bot that executed each order, NOT its current config,
// so a bot's orders placed under earlier configs stay visible after a new
// config is applied. Values are comma-separated lists (ORed server-side).
// This keeps the V3 pages untouched while still threading the fleet scope
// through every trading-data query.

export interface AgentFilter {
  agentIds: string[];
  configVersionIds: string[];
}

let agentFilter: AgentFilter = { agentIds: [], configVersionIds: [] };

export function setAgentFilter(next: AgentFilter): void {
  agentFilter = next;
}

export function getAgentFilter(): AgentFilter {
  return agentFilter;
}

export function withAgentParams(path: string): string {
  const { agentIds } = agentFilter;
  if (!agentIds.length) return path;
  const qIdx = path.indexOf("?");
  const p = qIdx >= 0 ? path.slice(0, qIdx) : path;
  if (!/^\/v2\/(orders|windows|analytics)(\/|$)/.test(p) && p !== "/v2/stats/analytics") return path;
  const sp = new URLSearchParams(qIdx >= 0 ? path.slice(qIdx + 1) : "");
  sp.set("agent_id", agentIds.join(","));
  return `${p}?${sp.toString()}`;
}

async function rawFetch<T>(
  schema: z.ZodType<T>,
  input: string,
  init: RequestInit,
  opts: ApiClientOpts,
): Promise<T> {
  const f = opts.fetchImpl ?? globalThis.fetch;
  const url = (opts.baseUrl ?? DEFAULT_BASE) + input;
  const res = await f(url, {
    ...init,
    headers: {
      "Accept": "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(init.headers ?? {}),
    },
  });
  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try { body = JSON.parse(text); }
    catch {
      throw new ApiClientError(res.status, "invalid_json",
        `non-JSON response (status=${res.status})`);
    }
  }
  if (!res.ok) {
    const e = ApiError.safeParse(body);
    if (e.success) {
      throw new ApiClientError(
        res.status, e.data.error.code, e.data.error.message,
        e.data.error.details, e.data.error.request_id,
      );
    }
    throw new ApiClientError(res.status, "http_error", `${res.status} ${res.statusText}`);
  }
  const parsed = schema.safeParse(body);
  if (!parsed.success) {
    // Surface schema drift loudly — contract violations should never silently slip.
    const issues = parsed.error.issues
      .slice(0, 5)
      .map((i) => `${i.path.join(".")}=${i.message}`)
      .join("; ");
    throw new ApiClientError(
      500, "schema_drift",
      `response failed schema validation: ${issues}`,
    );
  }
  return parsed.data;
}

const get = <T>(schema: z.ZodType<T>, path: string, opts: ApiClientOpts = {}) =>
  rawFetch(schema, withAgentParams(path), { method: "GET", ...(opts.signal ? { signal: opts.signal } : {}) }, opts);

const post = <T>(
  schema: z.ZodType<T>,
  path: string,
  body: unknown,
  opts: ApiClientOpts = {},
) =>
  rawFetch(
    schema,
    path,
    {
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...(opts.signal ? { signal: opts.signal } : {}),
    },
    opts,
  );

const patch = <T>(
  schema: z.ZodType<T>,
  path: string,
  body: unknown,
  opts: ApiClientOpts = {},
) =>
  rawFetch(
    schema,
    path,
    {
      method: "PATCH",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...(opts.signal ? { signal: opts.signal } : {}),
    },
    opts,
  );

const put = <T>(
  schema: z.ZodType<T>,
  path: string,
  body: unknown,
  opts: ApiClientOpts = {},
) =>
  rawFetch(
    schema,
    path,
    {
      method: "PUT",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...(opts.signal ? { signal: opts.signal } : {}),
    },
    opts,
  );

const del = <T>(schema: z.ZodType<T>, path: string, opts: ApiClientOpts = {}) =>
  rawFetch(
    schema,
    path,
    { method: "DELETE", ...(opts.signal ? { signal: opts.signal } : {}) },
    opts,
  );

export const api = {
  health: (o: ApiClientOpts = {}) => get(Health, "/v2/health", o),

  markets: {
    list: (o: ApiClientOpts = {}) => get(MarketList, "/v2/markets", o),
    get: (marketId: string, o: ApiClientOpts = {}) =>
      get(Market, `/v2/markets/${encodeURIComponent(marketId)}`, o),
    setEnabled: (marketId: string, enabled: boolean, o: ApiClientOpts = {}) =>
      patch(Market, `/v2/markets/${encodeURIComponent(marketId)}`, { enabled }, o),
    activeConfig: (marketId: string, o: ApiClientOpts = {}) =>
      get(MarketActiveConfig, `/v2/markets/${encodeURIComponent(marketId)}/config/active`, o),
    /**
     * V3 multi-market: activate a config snapshot for ``marketId``
     * only. Writes to ``config_activation_per_market``; the legacy
     * single-row pointer is left untouched so saving on btc-15m never
     * accidentally re-activates btc-5m.
     */
    activateConfig: (marketId: string, configVersionId: string, o: ApiClientOpts = {}) =>
      post(
        MarketConfigActivation,
        `/v2/markets/${encodeURIComponent(marketId)}/config/activate/${encodeURIComponent(configVersionId)}`,
        undefined,
        o,
      ),
  },

  live: {
    status: (o: ApiClientOpts = {}) => get(BotStatus, "/v2/live/status", o),
    window: (o: ApiClientOpts = {}) => get(LiveWindow, "/v2/live/window", o),
    // V3 multi-market endpoints.
    markets: (o: ApiClientOpts = {}) => get(LiveMarketsList, "/v2/live/markets", o),
    statusFor: (marketId: string, o: ApiClientOpts = {}) =>
      get(BotStatus, `/v2/live/markets/${encodeURIComponent(marketId)}/status`, o),
    windowFor: (marketId: string, o: ApiClientOpts = {}) =>
      get(LiveWindow, `/v2/live/markets/${encodeURIComponent(marketId)}/window`, o),
    manualBuy: (
      direction: z.infer<typeof Direction>,
      opts: { market_id?: string } = {},
      o: ApiClientOpts = {},
    ) =>
      post(ManualCommandResult, "/v2/live/manual-buy", {
        direction,
        ...(opts.market_id ? { market_id: opts.market_id } : {}),
      }, o),
    manualSell: (opts: { market_id?: string } = {}, o: ApiClientOpts = {}) =>
      post(
        ManualCommandResult,
        "/v2/live/manual-sell",
        opts.market_id ? { market_id: opts.market_id } : {},
        o,
      ),
  },

  windows: {
    list: (
      q: {
        cursor?: string;
        limit?: number;
        outcome?: "all" | "resolved" | "awaiting_resolution";
        market_id?: string;
      } = {},
      o: ApiClientOpts = {},
    ) => {
      const sp = new URLSearchParams();
      if (q.cursor) sp.set("cursor", q.cursor);
      if (q.limit != null) sp.set("limit", String(q.limit));
      if (q.outcome) sp.set("outcome", q.outcome);
      if (q.market_id) sp.set("market_id", q.market_id);
      const qs = sp.toString();
      return get(WindowPage, `/v2/windows${qs ? `?${qs}` : ""}`, o);
    },
    detail: (slug: string, o: ApiClientOpts = {}) => get(WindowDetail, `/v2/windows/${encodeURIComponent(slug)}`, o),
    ticks: (
      slug: string,
      range: { from?: string; to?: string } = {},
      o: ApiClientOpts = {},
    ) => {
      const sp = new URLSearchParams();
      if (range.from) sp.set("from", range.from);
      if (range.to) sp.set("to", range.to);
      const qs = sp.toString();
      return get(
        TicksResp,
        `/v2/windows/${encodeURIComponent(slug)}/ticks${qs ? `?${qs}` : ""}`,
        o,
      );
    },
    checks: (slug: string, o: ApiClientOpts = {}) => get(ChecksResp, `/v2/windows/${encodeURIComponent(slug)}/decision-checks`, o),
  },

  orders: {
    list: (
      q: {
        cursor?: string;
        limit?: number;
        mode?: "all" | "live" | "paper";
        status?: "all" | "open" | "filled" | "rejected" | "resolved";
        market_id?: string;
        /** V3.1: per-agent order view (see withAgentParams for auto-scoping). */
        agent_id?: string;
      } = {},
      o: ApiClientOpts = {},
    ) => {
      const sp = new URLSearchParams();
      if (q.cursor) sp.set("cursor", q.cursor);
      if (q.limit != null) sp.set("limit", String(q.limit));
      if (q.mode) sp.set("mode", q.mode);
      if (q.status) sp.set("status", q.status);
      if (q.market_id) sp.set("market_id", q.market_id);
      if (q.agent_id) sp.set("agent_id", q.agent_id);
      const qs = sp.toString();
      return get(OrderPage, `/v2/orders${qs ? `?${qs}` : ""}`, o);
    },
    detail: (id: string, o: ApiClientOpts = {}) => get(OrderDetail, `/v2/orders/${encodeURIComponent(id)}`, o),
  },

  stats: {
    running: (
      q: { mode?: "live" | "paper"; from?: string; to?: string; market_id?: string } = {},
      o: ApiClientOpts = {},
    ) => {
      const sp = new URLSearchParams();
      if (q.mode) sp.set("mode", q.mode);
      if (q.from) sp.set("from", q.from);
      if (q.to) sp.set("to", q.to);
      if (q.market_id) sp.set("market_id", q.market_id);
      const qs = sp.toString();
      return get(RunningStats, `/v2/stats/running${qs ? `?${qs}` : ""}`, o);
    },
    analytics: (
      q: {
        mode?: "live" | "paper" | "all";
        from?: string;
        to?: string;
        /** V3 multi-market: restrict the summary to one market id. */
        market_id?: string;
        /** V3.1: restrict to one engine group's decisions. */
        config_version_id?: string;
        /** All Markets counterpart-breakdown primary asset (default btc). */
        cp_primary?: "btc" | "eth";
      } = {},
      o: ApiClientOpts = {},
    ) => {
      const sp = new URLSearchParams();
      if (q.mode) sp.set("mode", q.mode);
      if (q.from) sp.set("from", q.from);
      if (q.to) sp.set("to", q.to);
      if (q.market_id) sp.set("market_id", q.market_id);
      if (q.config_version_id) sp.set("config_version_id", q.config_version_id);
      if (q.cp_primary) sp.set("cp_primary", q.cp_primary);
      const qs = sp.toString();
      return get(AnalyticsSummary, `/v2/stats/analytics${qs ? `?${qs}` : ""}`, o);
    },
  },

  config: {
    active: (o: ApiClientOpts = {}) => get(ConfigVersion, "/v2/config/active", o),
    byId: (id: string, o: ApiClientOpts = {}) =>
      get(ConfigVersion, `/v2/config/versions/${encodeURIComponent(id)}`, o),
    versions: (
      q: { cursor?: string; limit?: number; market_id?: string } = {},
      o: ApiClientOpts = {},
    ) => {
      const sp = new URLSearchParams();
      if (q.cursor) sp.set("cursor", q.cursor);
      if (q.limit != null) sp.set("limit", String(q.limit));
      if (q.market_id) sp.set("market_id", q.market_id);
      const qs = sp.toString();
      return get(ConfigVersionPage, `/v2/config/versions${qs ? `?${qs}` : ""}`, o);
    },
    versionStats: (q: { market_id?: string } = {}, o: ApiClientOpts = {}) => {
      const sp = new URLSearchParams();
      if (q.market_id) sp.set("market_id", q.market_id);
      const qs = sp.toString();
      return get(ConfigVersionStatsResponse, `/v2/config/version-stats${qs ? `?${qs}` : ""}`, o);
    },
    create: (cfg: Record<string, unknown>, notes?: string | null, o: ApiClientOpts = {}) =>
      post(ConfigVersion, "/v2/config/versions", { config: cfg, notes: notes ?? null }, o),
    activate: (id: string, o: ApiClientOpts = {}) =>
      post(ConfigActivation, `/v2/config/activate/${encodeURIComponent(id)}`, undefined, o),
    pruneInactive: (o: ApiClientOpts = {}) =>
      post(ConfigPruneResult, "/v2/config/prune-inactive", undefined, o),
  },

  grafanaDashboards: {
    /** Ordered list of registered Grafana dashboards. */
    list: (o: ApiClientOpts = {}) =>
      get(GrafanaDashboardListResp, "/v2/grafana/dashboards", o),
    /** Replace the entire dashboard list; returns the persisted order. */
    save: (items: z.infer<typeof GrafanaDashboardDto>[], o: ApiClientOpts = {}) =>
      put(GrafanaDashboardListResp, "/v2/grafana/dashboards", { items }, o),
  },

  analytics: {
    list: (
      q: {
        cursor?: string;
        limit?: number;
        date?: string;
        from?: string;
        to?: string;
        mode?: "all" | "live" | "paper";
        bet_status?: BetStatus | "all";
        show_hidden?: boolean;
        market_id?: string;
        /** V3.1: restrict to one engine group's decisions. */
        config_version_id?: string;
      } = {},
      o: ApiClientOpts = {},
    ) => {
      const sp = new URLSearchParams();
      if (q.cursor) sp.set("cursor", q.cursor);
      if (q.limit != null) sp.set("limit", String(q.limit));
      if (q.date) sp.set("date", q.date);
      if (q.from) sp.set("from", q.from);
      if (q.to) sp.set("to", q.to);
      if (q.mode) sp.set("mode", q.mode);
      if (q.bet_status) sp.set("bet_status", q.bet_status);
      if (q.show_hidden) sp.set("show_hidden", "true");
      if (q.market_id) sp.set("market_id", q.market_id);
      if (q.config_version_id) sp.set("config_version_id", q.config_version_id);
      const qs = sp.toString();
      return get(AnalyticsWindowPage, `/v2/analytics/windows${qs ? `?${qs}` : ""}`, o);
    },
    detail: (slug: string, o: ApiClientOpts = {}) =>
      get(AnalyticsWindowDetail, `/v2/analytics/windows/${encodeURIComponent(slug)}`, o),
    refetchOutcome: (slug: string, o: ApiClientOpts = {}) =>
      post(OutcomeProposal, `/v2/analytics/windows/${encodeURIComponent(slug)}/refetch-outcome`, undefined, o),
    applyOutcome: (
      slug: string,
      body: {
        winning_direction: "UP" | "DOWN";
        outcome_source?: string;
        net_pnl_usdc?: string | null;
        btc_start?: string | null;
        btc_end?: string | null;
        btc_delta?: string | null;
        create_order?: {
          direction: "UP" | "DOWN";
          filled_shares: string;
          size_usdc: string;
          price?: string | null;
          fee_usdc?: string | null;
          pnl_usdc?: string | null;
          mode?: "live" | "paper";
        };
        record_sell?: {
          exit_at: string;
          exit_reason: "take_profit" | "stop_loss" | "manual";
          sell_shares?: string | null;
          sell_price?: string | null;
          sell_usdc?: string | null;
          pnl_usdc?: string | null;
        };
      },
      o: ApiClientOpts = {},
    ) => post(OkResp, `/v2/analytics/windows/${encodeURIComponent(slug)}/apply-outcome`, body, o),
    setVisibility: (slug: string, hidden: boolean, o: ApiClientOpts = {}) =>
      post(WindowVisibilityResp, `/v2/analytics/windows/${encodeURIComponent(slug)}/visibility`, { hidden }, o),
  },

  simulations: {
    active: (q: { mode?: "single" | "sweep" } = {}, o: ApiClientOpts = {}) => {
      const sp = new URLSearchParams();
      if (q.mode) sp.set("mode", q.mode);
      const qs = sp.toString();
      return get(z.array(SimulationRun), `/v2/simulations/active${qs ? `?${qs}` : ""}`, o);
    },
    saved: (q: { mode?: "single" | "sweep"; target?: "auto" | "bullmq" | "modal"; limit?: number } = {}, o: ApiClientOpts = {}) => {
      const sp = new URLSearchParams();
      if (q.mode) sp.set("mode", q.mode);
      if (q.target) sp.set("target", q.target);
      if (q.limit != null) sp.set("limit", String(q.limit));
      const qs = sp.toString();
      return get(z.array(SimulationRun), `/v2/simulations/saved${qs ? `?${qs}` : ""}`, o);
    },
    modalReadiness: (o: ApiClientOpts = {}) =>
      get(ModalReadiness, "/v2/simulations/modal-readiness", o),
    gateTimings: (o: ApiClientOpts = {}) =>
      get(SimulationGateTimings, "/v2/simulations/gate-timings", o),
    modalResume: (body: z.infer<typeof ModalResumeRequest>, o: ApiClientOpts = {}) =>
      post(SimulationRun, "/v2/simulations/modal-resume", body, o),
    run: (body: z.infer<typeof SimulationRunRequest>, o: ApiClientOpts = {}) =>
      post(SimulationRun, "/v2/simulations/run", body, o),
    get: (runId: string, o: ApiClientOpts = {}) =>
      get(SimulationRun, `/v2/simulations/${encodeURIComponent(runId)}`, o),
    cancel: (runId: string, o: ApiClientOpts = {}) =>
      post(SimulationRun, `/v2/simulations/${encodeURIComponent(runId)}/cancel`, undefined, o),
    /** Pause a batched sweep parent (holds new slices; in-flight finishes). */
    pause: (runId: string, o: ApiClientOpts = {}) =>
      post(SimulationRun, `/v2/simulations/${encodeURIComponent(runId)}/pause`, undefined, o),
    /** Resume a paused batched sweep parent. */
    resume: (runId: string, o: ApiClientOpts = {}) =>
      post(SimulationRun, `/v2/simulations/${encodeURIComponent(runId)}/resume`, undefined, o),
    setSaved: (runId: string, saved: boolean, o: ApiClientOpts = {}) =>
      post(
        SimulationRun,
        `/v2/simulations/${encodeURIComponent(runId)}/save`,
        { saved },
        o,
      ),
    delete: (runId: string, o: ApiClientOpts = {}) =>
      del(SimulationDeleteResp, `/v2/simulations/${encodeURIComponent(runId)}`, o),
    /**
     * Re-run a saved simulation with its original request body.
     * Returns a NEW SimulationRun row (different run_id). The "Re-run"
     * button on Saved Simulations uses this to complete a sweep that
     * timed out or failed: the saved request_json is on disk in
     * simulation_runs.request_json, so we can resubmit it without
     * making the user open the Automated Simulator and re-enter the
     * combo space.
     */
    rerun: (runId: string, o: ApiClientOpts = {}) =>
      post(SimulationRun, `/v2/simulations/${encodeURIComponent(runId)}/rerun`, undefined, o),
    /**
     * Recover the partial top-K from a failed sweep's on-disk
     * batch_result_*.json artifacts. Spawns `sim-bot aggregate` on
     * the data-api side and updates simulation_results +
     * simulation_runs.result_summary_json with the merged finalists.
     * Only works for sweeps that ran after the per-batch artifact
     * rollout (older runs return a 400).
     */
    recover: (runId: string, o: ApiClientOpts = {}) =>
      post(SimulationRun, `/v2/simulations/${encodeURIComponent(runId)}/recover`, undefined, o),
    outcomeTrace: (
      runId: string,
      slug: string,
      range: { from?: string; to?: string } = {},
      o: ApiClientOpts = {},
    ) => {
      const sp = new URLSearchParams();
      if (range.from) sp.set("from", range.from);
      if (range.to) sp.set("to", range.to);
      const qs = sp.toString();
      return get(
        SimulationOutcomeTrace,
        `/v2/simulations/${encodeURIComponent(runId)}/outcomes/${encodeURIComponent(slug)}/trace${qs ? `?${qs}` : ""}`,
        o,
      );
    },
    /**
     * Exact per-feature impact for a finished manual run: replays the run
     * once per enabled feature (one engine run each), so this can take a
     * while on long date ranges. `leave_one_out` switches each feature off
     * against the full config; `standalone` switches each feature on
     * against the core-only config. Memoised server-side per mode.
     */
    gateAblation: (runId: string, mode: SimulationGateAblationMode = "leave_one_out", o: ApiClientOpts = {}) =>
      post(SimulationGateAblation, `/v2/simulations/${encodeURIComponent(runId)}/gate-ablation?mode=${mode}`, undefined, o),
  },

  kronos: {
    /** Per-market cache freshness + active-config signature. */
    status: (o: ApiClientOpts = {}) => get(KronosStatusResponse, "/v2/kronos/status", o),
    /**
     * List every precomputed cache available for a market (one row per
     * signature × decision_time, with parsed params + coverage). Powers the
     * "use this cache" picker in the simulators / config editor.
     */
    signatures: (marketId: string, o: ApiClientOpts = {}) =>
      get(
        KronosSignaturesResponse,
        `/v2/kronos/signatures?market_id=${encodeURIComponent(marketId)}`,
        o,
      ),
    /**
     * Start a precompute for one market. When the signature params are
     * provided they are used verbatim (precompute an arbitrary signature
     * without mutating the market's live config); omitted params fall back
     * to the active config, then the ConfigSnapshot defaults.
     */
    precompute: (
      body: {
        market_id: string;
        model?: "mini" | "small" | "base";
        lookback?: number;
        timeframe?: number;
        sample_count?: number;
        temperature?: number;
        top_p?: number;
        decision_times?: number[];
        overwrite?: boolean;
        limit?: number;
        /** ISO datetime lower bound; precompute every window from this point on. */
        start?: string;
        /** Settled-windows-only mode (forecast at window open; tags |md=settled). */
        settled?: boolean;
      },
      o: ApiClientOpts = {},
    ) => post(KronosJob, "/v2/kronos/precompute", body, o),
    /** Cancel the in-flight precompute for a market. */
    cancel: (marketId: string, o: ApiClientOpts = {}) =>
      post(z.object({ cancelled: z.boolean() }), "/v2/kronos/cancel", { market_id: marketId }, o),
    /**
     * Delete a cached signature for a market. Omit `decision_time_seconds` to
     * drop the whole signature, or set it to remove a single decision time.
     */
    deleteCache: (
      body: { market_id: string; signature: string; decision_time_seconds?: number },
      o: ApiClientOpts = {},
    ) => post(z.object({ deleted: z.number() }), "/v2/kronos/cache/delete", body, o),
  },

  sync: {
    /** Live/analytics DB split: current sync mode + last-run stats. */
    status: (o: ApiClientOpts = {}) => get(SyncStatus, "/v2/sync/status", o),
    /**
     * Run (or join) an incremental live -> analytics sync. Single-flight
     * on the server; resolves after the copy completes so callers can
     * immediately refetch fresh analytics data.
     */
    run: (o: ApiClientOpts = {}) => post(SyncRunResp, "/v2/sync", undefined, o),
  },

  /** Settings → Storage (1011_storage_tiering); admin-only on the data-api. */
  storage: {
    status: (o: ApiClientOpts = {}) => get(StorageStatus, "/v2/storage/status", o),
    scan: (o: ApiClientOpts = {}) => post(StorageStatus, "/v2/storage/scan", undefined, o),
    settings: (o: ApiClientOpts = {}) => get(StorageSettingsResponse, "/v2/settings/storage", o),
    updateSettings: (patch: Partial<StorageSettings>, o: ApiClientOpts = {}) =>
      put(StorageSettingsResponse, "/v2/settings/storage", patch, o),
    setMode: (action: StorageAction, mode: "notify_only" | "auto", o: ApiClientOpts = {}) =>
      put(z.object({ action_modes: ActionModes }), `/v2/storage/actions/${action}/mode`, { mode }, o),
    preview: (action: StorageAction, params: Record<string, unknown> = {}, o: ApiClientOpts = {}) =>
      post(StorageJob, `/v2/storage/actions/${action}/preview`, { params }, o),
    run: (action: StorageAction, params: Record<string, unknown> = {}, o: ApiClientOpts = {}) =>
      post(StorageJob, `/v2/storage/actions/${action}/run`, { params, confirm: action }, o),
    cancel: (o: ApiClientOpts = {}) => post(z.object({ cancelled: z.boolean() }), "/v2/storage/jobs/cancel", undefined, o),
    jobs: (o: ApiClientOpts = {}) => get(z.object({ items: z.array(StorageJob) }), "/v2/storage/jobs", o),
    archive: (o: ApiClientOpts = {}) => get(z.object({ items: z.array(ArchiveSegment) }), "/v2/storage/archive", o),
    restore: (segmentId: string, days?: number, o: ApiClientOpts = {}) =>
      post(StorageJob, `/v2/storage/archive/${encodeURIComponent(segmentId)}/restore`, days ? { days } : {}, o),
    extend: (segmentId: string, days: number, o: ApiClientOpts = {}) =>
      post(StorageJob, `/v2/storage/archive/${encodeURIComponent(segmentId)}/extend`, { days }, o),
    activationPreview: (marketId: string, configVersionId: string, o: ApiClientOpts = {}) =>
      get(ActivationPlan, `/v2/markets/${encodeURIComponent(marketId)}/config/activate-preview/${encodeURIComponent(configVersionId)}`, o),
  },

  ordersWrite: {
    edit: (
      orderId: string,
      body: {
        winning_direction?: "UP" | "DOWN" | null;
        outcome_source?: string | null;
        status?: "won" | "lost" | "no_fill";
        pnl_usdc?: string | null;
        size_usdc?: string | null;
        price?: string | null;
        filled_shares?: string | null;
        fee_usdc?: string | null;
        btc_open?: string | null;
        btc_close?: string | null;
        sold_early?: boolean;
        exit_reason?: "take_profit" | "stop_loss" | "manual" | "momentum_sell" | null;
      },
      o: ApiClientOpts = {},
    ) => patch(OkResp, `/v2/orders/${encodeURIComponent(orderId)}`, body, o),
    setVisibility: (orderId: string, hidden: boolean, o: ApiClientOpts = {}) =>
      post(VisibilityResp, `/v2/orders/${encodeURIComponent(orderId)}/visibility`, { hidden }, o),
  },
};
